import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import './CartPage.css';
import { getDeliverySettings } from '../utils/deliverySettings';
import { supabase } from '../config/supabase';

const CartPage = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliverySettings, setDeliverySettings] = useState({
    deliveryFee: 99,
    freeDeliveryAbove: 800,
    minOrderAmount: 300
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editAddress, setEditAddress] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  // Load cart, customer address, and delivery settings
  useEffect(() => {
    loadCart();
    loadCustomerAddress();
    loadDeliverySettings();
    // Listen for cart updates from other pages
    window.addEventListener('storage', loadCart);
    window.addEventListener('cartUpdated', loadCart);
    window.addEventListener('storage', loadCustomerAddress);
    return () => {
      window.removeEventListener('storage', loadCart);
      window.removeEventListener('cartUpdated', loadCart);
      window.removeEventListener('storage', loadCustomerAddress);
    };
  }, []);

  const loadDeliverySettings = async () => {
    try {
      const { data, error } = await getDeliverySettings();
      if (error) {
        console.error('Error loading delivery settings:', error);
        return;
      }
      if (data) {
        setDeliverySettings({
          deliveryFee: parseFloat(data.delivery_fee) || 99,
          freeDeliveryAbove: parseFloat(data.free_delivery_above) || 800,
          minOrderAmount: parseFloat(data.min_order_amount) || 300
        });
      }
    } catch (error) {
      console.error('Error loading delivery settings:', error);
    }
  };

  const loadCustomerAddress = () => {
    try {
      const customerData = localStorage.getItem('customer');
      if (customerData) {
        const customer = JSON.parse(customerData);
        if (customer.delivery_address) {
          setDeliveryAddress(customer.delivery_address);
          return;
        }
      }
      // Fallback to empty if no customer or no address
      setDeliveryAddress('');
    } catch (error) {
      console.error('Error loading customer address:', error);
      setDeliveryAddress('');
    }
  };

  const handleOpenAddressModal = () => {
    setEditAddress(deliveryAddress);
    setShowAddressModal(true);
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
    setEditAddress('');
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      // Fetch coupon from Supabase
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', couponCode.toUpperCase().trim())
        .eq('is_active', true)
        .single();

      if (error || !data) {
        setCouponError('Invalid or expired coupon code');
        setCouponLoading(false);
        return;
      }

      // Check if coupon is valid
      const now = new Date();
      const validFrom = new Date(data.valid_from);
      const validTill = new Date(data.valid_till);

      if (now < validFrom || now > validTill) {
        setCouponError('This coupon has expired');
        setCouponLoading(false);
        return;
      }

      // Check usage limit
      if (data.usage_limit && data.times_used >= data.usage_limit) {
        setCouponError('This coupon has reached its usage limit');
        setCouponLoading(false);
        return;
      }

      // Check minimum order amount
      const subtotal = getSubtotal();
      if (data.min_order_amount && subtotal < parseFloat(data.min_order_amount)) {
        setCouponError(`Minimum order amount of Rs. ${parseFloat(data.min_order_amount).toFixed(2)} required`);
        setCouponLoading(false);
        return;
      }

      // Transform coupon data
      const coupon = {
        id: data.id,
        code: data.code,
        description: data.description,
        discount_type: data.discount_type,
        discount_value: parseFloat(data.discount_value),
        max_discount: data.max_discount ? parseFloat(data.max_discount) : null,
        min_order_amount: data.min_order_amount ? parseFloat(data.min_order_amount) : null,
        applicable_to: data.applicable_to,
        applicable_categories: data.applicable_categories || [],
        applicable_items: data.applicable_items || []
      };

      setAppliedCoupon(coupon);
      setCouponError('');
      setCouponCode(''); // Clear input after successful application
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponError('Failed to apply coupon. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleSaveAddress = async () => {
    if (!editAddress.trim()) {
      alert('Please enter a delivery address');
      return;
    }

    try {
      const customerData = localStorage.getItem('customer');
      if (customerData) {
        const customer = JSON.parse(customerData);
        
        // Update address in localStorage
        const updatedCustomer = {
          ...customer,
          delivery_address: editAddress.trim()
        };
        localStorage.setItem('customer', JSON.stringify(updatedCustomer));

        // Update address in Supabase if customer is logged in
        if (customer.id) {
          const { error } = await supabase
            .from('customers')
            .update({ delivery_address: editAddress.trim() })
            .eq('id', customer.id);

          if (error) {
            console.error('Error updating address in Supabase:', error);
          }
        }

        setDeliveryAddress(editAddress.trim());
        setShowAddressModal(false);
      } else {
        // If not logged in, just update local state
        setDeliveryAddress(editAddress.trim());
        setShowAddressModal(false);
      }
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address. Please try again.');
    }
  };

  const loadCart = () => {
    try {
      const savedCart = localStorage.getItem('restaurantCart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setCart([]);
    }
  };

  const saveCart = (newCart) => {
    localStorage.setItem('restaurantCart', JSON.stringify(newCart));
    setCart(newCart);
    // Dispatch event to notify other components
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }

    const updatedCart = cart.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    saveCart(updatedCart);
  };

  const removeItem = (itemId) => {
    const updatedCart = cart.filter(item => item.id !== itemId);
    saveCart(updatedCart);
  };

  const clearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      saveCart([]);
    }
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getDeliveryFee = () => {
    const subtotal = getSubtotal();
    return subtotal >= deliverySettings.freeDeliveryAbove ? 0 : deliverySettings.deliveryFee;
  };

  const getDiscount = () => {
    if (!appliedCoupon) return 0;
    
    const subtotal = getSubtotal();
    
    // Check minimum order amount
    if (appliedCoupon.min_order_amount && subtotal < appliedCoupon.min_order_amount) {
      return 0;
    }
    
    let discount = 0;
    
    if (appliedCoupon.discount_type === 'percentage') {
      discount = (subtotal * appliedCoupon.discount_value) / 100;
      // Apply max discount if specified
      if (appliedCoupon.max_discount && discount > appliedCoupon.max_discount) {
        discount = appliedCoupon.max_discount;
      }
    } else if (appliedCoupon.discount_type === 'fixed') {
      discount = appliedCoupon.discount_value;
      // Don't allow discount more than subtotal
      if (discount > subtotal) {
        discount = subtotal;
      }
    }
    
    return discount;
  };

  const getTotal = () => {
    const subtotal = getSubtotal();
    const deliveryFee = getDeliveryFee();
    const discount = getDiscount();
    return subtotal + deliveryFee - discount;
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  return (
    <div className="cart-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        cartItemCount={getCartItemCount()}
        onCartClick={() => navigate('/cart')}
      />

      {/* Page Header */}
      <div className="cart-page-header">
        <div className="cart-page-header-container">
          <button
            className="cart-page-back-btn"
            onClick={() => navigate(-1)}
          >
            <span className="cart-page-back-icon">←</span>
            <span className="cart-page-back-text">Back</span>
          </button>
          <h1 className="cart-page-title">Your Cart</h1>
          {cart.length > 0 && (
            <button
              className="cart-page-clear-btn"
              onClick={clearCart}
            >
              Clear Cart
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="cart-page-main">
        <div className="cart-page-container">
          {cart.length === 0 ? (
            /* Empty Cart */
            <div className="cart-page-empty">
              <div className="cart-page-empty-icon">🛒</div>
              <h2 className="cart-page-empty-title">Your cart is empty</h2>
              <p className="cart-page-empty-subtitle">
                Add some delicious items to get started!
              </p>
              <button
                className="cart-page-shop-btn"
                onClick={() => navigate('/menu')}
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <div className="cart-page-content">
              {/* Left Column - Cart Items */}
              <div className="cart-page-items-section">
                <h2 className="cart-page-section-title">
                  Items ({getCartItemCount()})
                </h2>

                <div className="cart-page-items-list">
                  {cart.map((item) => (
                    <div key={item.id} className="cart-page-item-card">
                      <div className="cart-page-item-image-container">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="cart-page-item-image"
                            onClick={() => navigate(`/item/${item.id}`)}
                          />
                        ) : (
                          <div className="cart-page-item-image-placeholder">🍽️</div>
                        )}
                      </div>

                      <div className="cart-page-item-details">
                        <h3
                          className="cart-page-item-name"
                          onClick={() => navigate(`/item/${item.id}`)}
                        >
                          {item.name}
                        </h3>
                        {item.category && (
                          <span className="cart-page-item-category">{item.category}</span>
                        )}
                        <div className="cart-page-item-price-row">
                          <span className="cart-page-item-price">
                            Rs. {item.price.toFixed(2)}
                          </span>
                          {item.originalPrice && item.originalPrice > item.price && (
                            <span className="cart-page-item-price-original">
                              Rs. {item.originalPrice.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="cart-page-item-controls">
                        <div className="cart-page-quantity-controls">
                          <button
                            className="cart-page-qty-btn cart-page-qty-minus"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          >
                            −
                          </button>
                          <span className="cart-page-quantity-display">
                            {item.quantity}
                          </span>
                          <button
                            className="cart-page-qty-btn cart-page-qty-plus"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            +
                          </button>
                        </div>
                        <div className="cart-page-item-total">
                          Rs. {(item.price * item.quantity).toFixed(2)}
                        </div>
                        <button
                          className="cart-page-remove-btn"
                          onClick={() => removeItem(item.id)}
                          aria-label="Remove item"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="cart-page-summary-section">
                <div className="cart-page-summary-card">
                  <h2 className="cart-page-summary-title">Order Summary</h2>

                  {/* Delivery Address */}
                  <div className="cart-page-address-section">
                    <div className="cart-page-address-header">
                      <span className="cart-page-address-icon">📍</span>
                      <span className="cart-page-address-label">Delivery Address</span>
                    </div>
                    <p className="cart-page-address-text">{deliveryAddress}</p>
                    <button 
                      className="cart-page-change-address-btn"
                      onClick={handleOpenAddressModal}
                    >
                      Change
                    </button>
                  </div>

                  {/* Coupon Section */}
                  <div className="cart-page-coupon-section">
                    {!appliedCoupon ? (
                      <div className="cart-page-coupon-input-group">
                        <input
                          type="text"
                          className="cart-page-coupon-input"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value.toUpperCase());
                            setCouponError('');
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleApplyCoupon();
                            }
                          }}
                        />
                        <button
                          className="cart-page-coupon-apply-btn"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading}
                        >
                          {couponLoading ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                    ) : (
                      <div className="cart-page-coupon-applied">
                        <div className="cart-page-coupon-applied-info">
                          <span className="cart-page-coupon-code">🎟️ {appliedCoupon.code}</span>
                          <span className="cart-page-coupon-discount">
                            {appliedCoupon.discount_type === 'percentage'
                              ? `${appliedCoupon.discount_value}% OFF`
                              : `Rs. ${appliedCoupon.discount_value.toFixed(2)} OFF`}
                          </span>
                        </div>
                        <button
                          className="cart-page-coupon-remove-btn"
                          onClick={handleRemoveCoupon}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {couponError && (
                      <div className="cart-page-coupon-error">{couponError}</div>
                    )}
                  </div>

                  {/* Order Details */}
                  <div className="cart-page-order-details">
                    <div className="cart-page-order-row">
                      <span className="cart-page-order-label">Subtotal</span>
                      <span className="cart-page-order-value">
                        Rs. {getSubtotal().toFixed(2)}
                      </span>
                    </div>
                    {appliedCoupon && getDiscount() > 0 && (
                      <div className="cart-page-order-row cart-page-discount-row">
                        <span className="cart-page-order-label">
                          Discount ({appliedCoupon.code})
                        </span>
                        <span className="cart-page-order-value cart-page-discount-value">
                          - Rs. {getDiscount().toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="cart-page-order-row">
                      <span className="cart-page-order-label">Delivery Fee</span>
                      <span className="cart-page-order-value">
                        {getDeliveryFee() === 0 ? (
                          <span className="cart-page-free-delivery">Free</span>
                        ) : (
                          `Rs. ${getDeliveryFee().toFixed(2)}`
                        )}
                      </span>
                    </div>
                    {getSubtotal() < deliverySettings.freeDeliveryAbove && (
                      <div className="cart-page-delivery-note">
                        Add Rs. {(deliverySettings.freeDeliveryAbove - getSubtotal()).toFixed(2)} more for free delivery!
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  <div className="cart-page-total-section">
                    <div className="cart-page-total-row">
                      <span className="cart-page-total-label">Total</span>
                      <span className="cart-page-total-amount">
                        Rs. {getTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <button
                    className="cart-page-checkout-btn"
                    onClick={handleCheckout}
                  >
                    <span className="cart-page-checkout-icon">🛒</span>
                    <span>Proceed to Checkout</span>
                  </button>

                  {/* Continue Shopping */}
                  <button
                    className="cart-page-continue-btn"
                    onClick={() => navigate('/menu')}
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Address Edit Modal */}
      {showAddressModal && (
        <div className="cart-page-modal-overlay" onClick={handleCloseAddressModal}>
          <div className="cart-page-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cart-page-modal-header">
              <h2 className="cart-page-modal-title">Edit Delivery Address</h2>
              <button 
                className="cart-page-modal-close"
                onClick={handleCloseAddressModal}
              >
                ×
              </button>
            </div>
            <div className="cart-page-modal-content">
              <div className="cart-page-modal-form-group">
                <label className="cart-page-modal-label">
                  Delivery Address <span className="cart-page-modal-required">*</span>
                </label>
                <textarea
                  className="cart-page-modal-textarea"
                  placeholder="Enter your delivery address"
                  rows={4}
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                />
              </div>
            </div>
            <div className="cart-page-modal-footer">
              <button
                className="cart-page-modal-cancel-btn"
                onClick={handleCloseAddressModal}
              >
                Cancel
              </button>
              <button
                className="cart-page-modal-save-btn"
                onClick={handleSaveAddress}
              >
                Save Address
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;

